import { useEffect, useMemo, useState } from "react";

import { ReactComponent as QontactPointSVG } from "@assets/SVGs/QontactPoint/QontactPointSVG.svg";
import { QontyEmptyState } from "@components/QontyEmptyState";
import { QontyLoading } from "@components/QontyLoading";
import UserTurno from "@components/Colaboradores/UserTurno";
import { usePopup } from "@contexts/popupContext";
import { Application } from "@core/Application";
import { ShiftRecipe } from "@models/QontactPoint";
import { DedicatedShiftRecipePayload } from "@services/UserDedicatedShiftsService";
import { HollowedButton, IconButton, NormalButton } from "@shared/designSystem/Button";
import { Loader } from "@shared/designSystem/Loader";
import { ToastManager } from "@shared/designSystem/messages/ToastManager";
import popupStyles from "@styles/components/Novedades/RemoverColaborador.module.css";
import styles from "@styles/screens/ColaboradorDedicatedShiftsScreen.module.css";

import {
    EditableDedicatedShift,
    useColaboradorDedicatedShiftsViewModel,
} from "./ColaboradorDedicatedShiftsViewModel";

interface ColaboradorDedicatedShiftsScreenProps {
    orgID: string;
    colaborador: {
        id?: string;
        name?: string;
    };
    hideTitle?: boolean;
    readOnly?: boolean;
}

interface ShiftRowProps {
    orgID: string;
    collaboratorID: string;
    shift: EditableDedicatedShift;
    onRefresh: () => Promise<void>;
    onRemoveUnsavedShift: (localId: string) => void;
    readOnly?: boolean;
}

function normalizeTime(time: string) {
    const [hour, minute] = time.split(":");
    const normalizedHour = parseInt(hour, 10).toString();
    return `${normalizedHour}:${minute}`;
}

function getShiftType(shift: ShiftRecipe): ShiftRecipe["type"] {
    const weekdays = shift.configurationData?.weekdays ?? [];
    if (!weekdays.length || weekdays.length === 7) {
        return "daily";
    }
    return "weekly";
}

function validateShift(shift: EditableDedicatedShift) {
    if (!shift.name.trim() || !shift.hourFrom.trim() || !shift.hourTo.trim()) {
        return "No pueden haber campos vacios.";
    }

    return "";
}

const DedicatedShiftRow: React.FC<ShiftRowProps> = ({
    orgID,
    collaboratorID,
    shift,
    onRefresh,
    onRemoveUnsavedShift,
    readOnly = false,
}) => {
    const { showPopup } = usePopup();
    const [draftShift, setDraftShift] = useState<EditableDedicatedShift>(shift);
    const [initialShift, setInitialShift] = useState<EditableDedicatedShift>(shift);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        setDraftShift(shift);
        setInitialShift(shift);
        setError("");
    }, [shift]);

    const isDirty = useMemo(
        () => JSON.stringify(draftShift) !== JSON.stringify(initialShift),
        [draftShift, initialShift]
    );
    const shouldShowInlineActions = !readOnly && (draftShift.isNew || isDirty);

    const handleCancel = () => {
        if (draftShift.isNew) {
            onRemoveUnsavedShift(draftShift.localId);
            return;
        }

        setDraftShift(initialShift);
        setError("");
    };

    const handleSave = async () => {
        const validationError = validateShift(draftShift);
        if (validationError) {
            setError(validationError);
            return;
        }

        setSaving(true);
        setError("");

        try {
            const dedicatedShiftService = Application.shared().coreDependencies.userDedicatedShiftsService;
            const payload: DedicatedShiftRecipePayload = {
                name: draftShift.name,
                hourFrom: normalizeTime(draftShift.hourFrom),
                hourTo: normalizeTime(draftShift.hourTo),
                expected: draftShift.expected,
                tolerance: draftShift.tolerance,
                type: getShiftType(draftShift),
                configurationData: draftShift.configurationData,
                expirationType: draftShift.expirationType,
                startTimestamp: draftShift.startTimestamp,
                endTimestamp: draftShift.endTimestamp,
                firestoreRecordId: draftShift.firestoreRecordId,
            };

            if (draftShift.firestoreRecordId) {
                await dedicatedShiftService.updateShiftRecipe(
                    orgID,
                    collaboratorID,
                    draftShift.firestoreRecordId,
                    payload
                );
            } else {
                await dedicatedShiftService.createShiftRecipe(orgID, collaboratorID, payload);
            }

            ToastManager.shared().showSuccessMessage("Turno guardado correctamente".localized());
            await onRefresh();
        } catch (saveError) {
            console.error(saveError);
            setError("Error al intentar guardar el turno.");
            ToastManager.shared().showErrorMessage("Error al intentar guardar turno".localized());
        } finally {
            setSaving(false);
        }
    };

    const deleteShift = async () => {
        if (draftShift.isNew || !draftShift.firestoreRecordId) {
            return;
        }

        setSaving(true);
        setError("");
        try {
            const dedicatedShiftService = Application.shared().coreDependencies.userDedicatedShiftsService;
            await dedicatedShiftService.deleteShiftRecipe(
                orgID,
                collaboratorID,
                draftShift.firestoreRecordId
            );
        } catch (deleteError) {
            console.error(deleteError);
            setError("Error al intentar eliminar el turno.");
            throw deleteError;
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = () => {
        showPopup(
            () => (
                <DeleteShiftConfirmationPopup
                    title="Eliminar turno"
                    message={`¿Estás seguro que querés eliminar el turno ${draftShift.name || ""}?`}
                    onDelete={() => {
                        if (draftShift.isNew || !draftShift.firestoreRecordId) {
                            return Promise.resolve();
                        }

                        return deleteShift();
                    }}
                    onSuccess={async () => {
                        if (draftShift.isNew || !draftShift.firestoreRecordId) {
                            onRemoveUnsavedShift(draftShift.localId);
                            return;
                        }

                        await onRefresh();
                    }}
                    confirmButtonText="Eliminar"
                />
            ),
            false
        );
    };

    return (
        <div className={styles["shift-card"]}>
            <div className={styles["shift-card-body"]}>
                <div className={styles["editor-column"]}>
                    <UserTurno
                        key={draftShift.localId}
                        turno={draftShift}
                        onChange={(updatedTurno) =>
                            setDraftShift((current) => ({
                                ...current,
                                ...updatedTurno,
                            }))
                        }
                        isEditable={!readOnly}
                        isItemFromList={false}
                        errorMessage={error}
                    />
                </div>

                <div className={styles["actions-column"]}>
                    {!readOnly && !draftShift.isNew ? (
                        <div className={styles["row-actions"]}>
                            <IconButton
                                iconType="delete"
                                tooltip={"Eliminar".localized()}
                                onClick={handleDelete}
                                disabled={saving}
                            />
                        </div>
                    ) : null}

                    {shouldShowInlineActions && (
                        <div className={styles["inline-actions"]}>
                            <HollowedButton
                                text={"Cancelar".localized()}
                                onClick={handleCancel}
                                disabled={saving}
                                width="128px"
                            />
                            <NormalButton
                                text={"Guardar".localized()}
                                onClick={handleSave}
                                loading={saving}
                                width="128px"
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

interface DeleteShiftConfirmationPopupProps {
    title: string;
    message: string;
    onDelete: () => Promise<void>;
    onSuccess?: () => Promise<void> | void;
    confirmButtonText: string;
}

const DeleteShiftConfirmationPopup: React.FC<DeleteShiftConfirmationPopupProps> = ({
    title,
    message,
    onDelete,
    onSuccess,
    confirmButtonText,
}) => {
    const { hidePopup } = usePopup();
    const [loading, setLoading] = useState(false);

    const handleConfirmDelete = async () => {
        setLoading(true);

        try {
            await onDelete();
            hidePopup();
            await onSuccess?.();
            ToastManager.shared().showSuccessMessage("Turno eliminado correctamente".localized());
        } catch (error) {
            console.error(error);
            ToastManager.shared().showErrorMessage("Error al intentar eliminar turno".localized());
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={popupStyles["popup"]}>
            <div className={popupStyles["title"]}>{title}</div>
            <div className={popupStyles["warning-text"]}>{message}</div>

            <div className={popupStyles["bottom-container"]}>
                <div className={`${popupStyles["button"]} ${popupStyles["cancelar"]}`} onClick={hidePopup}>
                    {"Cancelar".localized()}
                </div>

                <div
                    className={`${popupStyles["button"]} ${popupStyles["agregar"]}`}
                    onClick={handleConfirmDelete}
                >
                    {!loading ? (
                        confirmButtonText
                    ) : (
                        <div className={popupStyles["no-qontact-points"]}>
                            <Loader height="32px" width="32px" />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const ColaboradorDedicatedShiftsScreen: React.FC<ColaboradorDedicatedShiftsScreenProps> = ({
    orgID,
    colaborador,
    hideTitle = false,
    readOnly = false,
}) => {
    const collaboratorID = colaborador?.id;
    const {
        loading,
        refreshing,
        shifts,
        addEmptyShift,
        removeUnsavedShift,
        refreshShifts,
    } = useColaboradorDedicatedShiftsViewModel({
        orgID,
        collaboratorID,
    });

    return (
        <div className={styles["main-container"]}>
            <div className={styles["header-row"]}>
                {!hideTitle ? (
                    <div className={styles["title-group"]}>
                        <div className={styles["icon-container"]}>
                            <QontactPointSVG />
                        </div>
                        <h2 className={styles["title"]}>Turnos dedicados</h2>
                    </div>
                ) : null}

                {!readOnly ? (
                    <div className={styles["header-actions"]}>
                        <NormalButton
                            text={"Agregar turno".localized()}
                            onClick={addEmptyShift}
                            disabled={!collaboratorID || shifts.some((shift) => shift.isNew)}
                            width="180px"
                        />
                    </div>
                ) : null}
            </div>

            {loading ? (
                <div className={styles["state-container"]}>
                    <QontyLoading
                        text={"Cargando turnos dedicados...".localized()}
                        withQonty={true}
                        darkMode={false}
                    />
                </div>
            ) : !collaboratorID ? (
                <div className={styles["state-container"]}>
                    <QontyEmptyState text={"No se encontró el colaborador.".localized()} darkMode={false} />
                </div>
            ) : shifts.length === 0 ? (
                <div className={styles["state-container"]}>
                    <QontyEmptyState text={"Este colaborador no tiene turnos dedicados aún.".localized()} darkMode={false} />
                </div>
            ) : (
                <div className={styles["list-container"]}>
                    {shifts.map((shift) => (
                        <DedicatedShiftRow
                            key={shift.localId}
                            orgID={orgID}
                            collaboratorID={collaboratorID}
                            shift={shift}
                            onRefresh={refreshShifts}
                            onRemoveUnsavedShift={removeUnsavedShift}
                            readOnly={readOnly}
                        />
                    ))}
                </div>
            )}

            {refreshing && !loading && (
                <div className={styles["refreshing-label"]}>Actualizando turnos...</div>
            )}
        </div>
    );
};

export default ColaboradorDedicatedShiftsScreen;
